import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Camera, Trash } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { OBJETS, TYPE_ELEMENT } from '../../lib/libelles';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * RP14 (ARC-R5) — « + Action » : création d'une action depuis la liste unique
 * « Actions » du bureau (POST /chantiers/{cid}/elements, etat « À faire »).
 *
 * Mêmes sources que la validation d'une proposition (TaskValidateModal) :
 * GET /chantiers/{cid}/equipe (entreprises) et /lots. L'entreprise est enregistrée
 * dans `entreprise` (nom) + `entreprise_user_ids`, le lot dans `lot` (nom).
 *
 * Fluidité A3 (G4) — photo jointe dès la création : envoyée au chantier
 * (POST /chantiers/{cid}/upload, hors Fil), puis rattachée à l'action (`media_url`).
 */

// Fluidité B4 (P5#17) — « Garantie (GPA) » : la demande passe par /gpa, qui retrouve la
// réception, le lot et l'entreprise (prévenue même sans compte) et contrôle le délai.
const TYPES = ['tache', 'reserve', 'non_conformite', 'incident', 'question_entreprise', 'gpa'];
const PRIORITES = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'normal', label: 'Normal' },
  { value: 'basse', label: 'Basse' },
];

const VIDE = { titre: '', description: '', type: 'tache', priorite: 'normal', echeance: '', entrepriseUserId: '', lotNom: '', lieu: '', inclureCRC: true };

export default function ActionCreateDialog({ open, onClose, chantierId, onCreated }) {
  const [form, setForm] = useState(VIDE);
  const [equipe, setEquipe] = useState([]);
  const [lots, setLots] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [photo, setPhoto] = useState(null);
  const [apercu, setApercu] = useState(null);
  const photoRef = useRef(null);

  useEffect(() => {
    if (!open || !chantierId) return;
    setForm(VIDE);
    setError('');
    setPhoto(null);
    setApercu(null);
    Promise.all([
      axios.get(`${API_URL}/api/chantiers/${chantierId}/equipe`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/chantiers/${chantierId}/lots`, { withCredentials: true }).catch(() => ({ data: [] })),
    ]).then(([eq, lt]) => {
      setEquipe(Array.isArray(eq.data) ? eq.data : []);
      setLots(Array.isArray(lt.data) ? lt.data : []);
    });
  }, [open, chantierId]);

  const entreprises = useMemo(() => equipe.filter((m) => {
    const role = m.role_projet || m.role || '';
    return role === 'entreprise' || role === 'employe_entreprise';
  }), [equipe]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const choisirPhoto = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { setError('Photo trop volumineuse (10 Mo au plus).'); return; }
    setPhoto(f);
    try { setApercu(URL.createObjectURL(f)); } catch { setApercu(null); }
  };

  const choisirEntreprise = (uid) => {
    const ent = entreprises.find((m) => String(m.user_id) === uid);
    setForm((f) => ({
      ...f,
      entrepriseUserId: uid,
      lotNom: !f.lotNom && ent && Array.isArray(ent.lots) && ent.lots.length === 1
        ? (ent.lots[0].nom || ent.lots[0].code || '') : f.lotNom,
    }));
  };

  const creer = async () => {
    if (!form.titre.trim()) {
      setError('Le titre est requis.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const ent = entreprises.find((m) => String(m.user_id) === form.entrepriseUserId);
      const lot = lots.find((l) => l.nom === form.lotNom);
      const entrepriseNom = (ent && (ent.entreprise_nom || ent.name))
        || (!form.entrepriseUserId && lot && lot.entreprise_nom) || null;
      let mediaUrl = null;
      if (photo) {
        const fd = new FormData();
        fd.append('file', photo, photo.name || `action_${Date.now()}.jpg`);
        fd.append('doc_type', 'photo');
        fd.append('force_duplicate', 'true');
        // L'entreprise concernée doit voir la photo de son action (fichier non public).
        fd.append('visible_to', JSON.stringify(['archi', 'moe_principal', 'moe_membre', 'consultant', 'entreprise']));
        fd.append('notes', `Photo de l'action « ${form.titre.trim().slice(0, 80)} »`);
        const up = await axios.post(`${API_URL}/api/chantiers/${chantierId}/upload`, fd, { withCredentials: true });
        mediaUrl = up?.data?.url || up?.data?.media_url || null;
        if (!mediaUrl) throw new Error("La photo n'a pas pu être envoyée : réessayez.");
      }
      if (form.type === 'gpa') {
        const lotGpa = lots.find((l) => l.nom === form.lotNom);
        const { data } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/gpa`, {
          titre: form.titre.trim().slice(0, 200),
          description: (form.description.trim() || form.titre.trim()),
          lot: lotGpa ? (lotGpa.code || lotGpa.nom) : null,
          localisation: form.lieu.trim() || null,
          photo_url: mediaUrl,
        }, { withCredentials: true });
        toast.success(data.entreprise ? `Demande en GPA transmise à ${data.entreprise}` : 'Demande en GPA enregistrée : choisissez le lot pour la transmettre');
        onCreated?.(data);
        onClose?.();
        return;
      }
      const { data } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/elements`, {
        chantier_id: chantierId,
        source_id: 'creation_manuelle',
        type: form.type,
        titre: form.titre.trim().slice(0, 200),
        description: form.description.trim() || null,
        priorite: form.priorite,
        echeance: form.echeance || null,
        entreprise: entrepriseNom,
        entreprise_user_ids: form.entrepriseUserId ? [form.entrepriseUserId] : [],
        lot: form.lotNom || null,
        lieu: form.lieu.trim() || null,
        media_url: mediaUrl,
        media_kind: mediaUrl ? 'photo' : null,
        inclure_dans_prochain_crc: form.inclureCRC,
      }, { withCredentials: true });
      toast.success('Action créée');
      onCreated?.(data);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "L'action n'a pas pu être créée.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-lg rounded-none" data-testid="action-create-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus size={18} weight="bold" /> Nouvelle {OBJETS.action.toLowerCase()}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <Label htmlFor="action-titre">Titre *</Label>
            <Input id="action-titre" value={form.titre} maxLength={200} onChange={(e) => set('titre', e.target.value)} className="rounded-none" data-testid="action-create-titre" />
          </div>
          <div>
            <Label htmlFor="action-description">Description</Label>
            <Textarea id="action-description" rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} className="rounded-none" data-testid="action-create-description" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Type</Label>
              <select value={form.type} onChange={(e) => set('type', e.target.value)} className="w-full px-2 py-2 border border-[#E0E0DB] text-sm bg-white" data-testid="action-create-type">
                {TYPES.map((t) => <option key={t} value={t}>{TYPE_ELEMENT[t] || t}</option>)}
              </select>
            </div>
            <div>
              <Label>Priorité</Label>
              <select value={form.priorite} onChange={(e) => set('priorite', e.target.value)} className="w-full px-2 py-2 border border-[#E0E0DB] text-sm bg-white" data-testid="action-create-priorite">
                {PRIORITES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Entreprise</Label>
              <select value={form.entrepriseUserId} onChange={(e) => choisirEntreprise(e.target.value)} className="w-full px-2 py-2 border border-[#E0E0DB] text-sm bg-white" data-testid="action-create-entreprise">
                <option value="">— À désigner —</option>
                {entreprises.map((m) => (
                  <option key={m.user_id} value={String(m.user_id)}>{m.entreprise_nom || m.name || m.email}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Lot</Label>
              <select value={form.lotNom} onChange={(e) => set('lotNom', e.target.value)} className="w-full px-2 py-2 border border-[#E0E0DB] text-sm bg-white" data-testid="action-create-lot">
                <option value="">— Aucun —</option>
                {lots.map((l) => <option key={l.id || l.nom} value={l.nom}>{l.code ? `${l.code} — ${l.nom}` : l.nom}</option>)}
              </select>
            </div>
            <div>
              <Label htmlFor="action-echeance">Échéance</Label>
              <Input id="action-echeance" type="date" value={form.echeance} onChange={(e) => set('echeance', e.target.value)} className="rounded-none" data-testid="action-create-echeance" />
            </div>
            <div>
              <Label htmlFor="action-lieu">Lieu</Label>
              <Input id="action-lieu" value={form.lieu} onChange={(e) => set('lieu', e.target.value)} className="rounded-none" placeholder="Pièce, niveau…" />
            </div>
          </div>
          <div>
            <Label>Photo</Label>
            <input ref={photoRef} type="file" accept="image/*" onChange={choisirPhoto} className="hidden" data-testid="action-create-photo-input" />
            {apercu ? (
              <div className="relative" data-testid="action-create-photo-apercu">
                <img src={apercu} alt="" className="w-full max-h-40 object-cover" />
                <button type="button" onClick={() => { setPhoto(null); setApercu(null); }} className="absolute top-1 right-1 bg-black/60 text-white p-2" aria-label="Retirer la photo">
                  <Trash size={14} />
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => photoRef.current?.click()} className="w-full border border-dashed border-[#9A9A9A] bg-[#FAFAF8] py-2 text-sm flex items-center justify-center gap-2" data-testid="action-create-photo">
                <Camera size={16} /> Joindre une photo
              </button>
            )}
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.inclureCRC} onChange={(e) => set('inclureCRC', e.target.checked)} data-testid="action-create-crc" />
            Reprendre au prochain compte rendu
          </label>
          {error && <p className="text-xs text-[#C00000]" data-testid="action-create-error">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" className="rounded-none" onClick={onClose} disabled={submitting}>Annuler</Button>
            <Button className="rounded-none bg-[#1E3A5F] hover:bg-[#2C4F7C]" onClick={creer} disabled={submitting} data-testid="action-create-submit">
              {submitting ? 'Création…' : 'Créer l\'action'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
